import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useState } from 'react';
import { riskAnalysis, type AnalizaMeta, type AnalizaExtractResult } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { validateUploadSize, type UploadProgress } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';

/**
 * Prefill danych analizy ryzyka z arkusza „Dane do analizy" (XLSX). Upload → odczyt AI (propozycja,
 * bez zapisu) → edytowalna siatka zatwierdzenia → scalenie do formularza. Wzorzec ExtractionReviewModal.
 */
export function AnalizaExtractModal({ studyId, meta, onClose, onConfirm }: {
  studyId: number;
  meta: AnalizaMeta;
  onClose: () => void;
  onConfirm: (fields: Record<string, unknown>) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<AnalizaExtractResult | null>(null);
  const [edited, setEdited] = useState<Record<string, unknown>>({});
  const [uploadPct, setUploadPct] = useState<number | null>(null); // null = brak wysyłki

  const labelFor = (key: string) => meta.fields.find(f => f.key === key)?.label ?? key;

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    // Limit rozmiaru pliku (100 MB) — blokada przed wysyłką
    const sizeErr = validateUploadSize(file);
    if (sizeErr) { setErr(sizeErr); return; }
    setBusy(true); setErr(null); setUploadPct(0);
    try {
      const r = await riskAnalysis.extract(studyId, file, (p: UploadProgress) => setUploadPct(p.percent));
      setResult(r);
      setEdited({ ...r.fields });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false); setUploadPct(null);
    }
  };

  const keys = result ? Object.keys(edited) : [];

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { result, edited }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-ink-100">
          <Icon name="sparkles" className="w-4 h-4 text-ai-500"/>
          <div className="font-semibold">Wypełnij z arkusza „Dane do analizy" (AI)</div>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-3">
          {!result && (
            <label className="block border-2 border-dashed border-ink-200 rounded-lg p-6 text-center cursor-pointer hover:border-water-400">
              <input type="file" className="hidden" accept=".xlsx,.xls,.csv"
                     onChange={e => onFile(e.target.files?.[0])} disabled={busy}/>
              <Icon name="download" className="w-6 h-6 mx-auto text-ink-300 mb-2"/>
              <div className="text-[13px] text-ink-600">
                {busy ? 'Odczytuję arkusz…' : 'Kliknij i wybierz plik XLSX z danymi ujęcia'}
              </div>
            </label>
          )}

          {/* Pasek postępu wysyłki i analizy pliku */}
          {uploadPct !== null && <UploadProgressBar percent={uploadPct} label="Wysyłanie i analiza…" />}

          {err && <div className="text-red-500 text-[13px]">Błąd: {err}</div>}

          {result && (
            <>
              {result.mock && (
                <div className="pill pill-amber text-[12px]">
                  Tryb demonstracyjny (AI nieskonfigurowane) — wykryto tylko podstawowe pola heurystycznie.
                </div>
              )}
              <div className="text-[12px] text-ink-500">
                Znaleziono {keys.length} pól ({result.provider}/{result.model}). Zweryfikuj i popraw przed zatwierdzeniem.
              </div>
              {keys.length === 0 && <div className="text-ink-400 text-[13px]">AI nie znalazło pól do wypełnienia.</div>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {keys.map(k => (
                  <label key={k} className="field">
                    <span className="field-label">{labelFor(k)}</span>
                    <input className="input"
                           value={typeof edited[k] === 'object' ? JSON.stringify(edited[k]) : String(edited[k] ?? '')}
                           onChange={e => setEdited(prev => ({ ...prev, [k]: e.target.value }))}/>
                  </label>
                ))}
              </div>
              <details className="text-[12px]">
                <summary className="cursor-pointer text-ink-500">Podgląd tekstu źródłowego</summary>
                <pre className="mt-1 p-2 bg-ink-50 rounded text-[11px] whitespace-pre-wrap max-h-40 overflow-y-auto">{result.sourceTextPreview}</pre>
              </details>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-ink-100">
          <button onClick={requestClose} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={() => onConfirm(edited)} disabled={!result || keys.length === 0}
                  className="btn btn-primary btn-sm">
            <Icon name="check" className="w-3.5 h-3.5"/> Zatwierdź i wypełnij formularz
          </button>
        </div>
      </div>
    </div>
  );
}
